import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import api from '../services/api'
import { getToken } from '../services/token'
import { formatDate as fmtDate, formatTime as fmtTime } from '../utils/format'

const STORAGE_KEY = 'lextrack_theme_preference'

const DEFAULTS = {
  theme: 'light',
  accent_color: 'purple',
  font_size: 'medium',
  sidebar_collapsed: false,
  compact_mode: false,
  border_radius: 'rounded',
  shadow_style: 'subtle',
  border_style: 'normal',
  sidebar_style: 'flush',
  sidebar_width: 'normal',
  table_density: 'comfortable',
  date_format: 'MM/DD/YYYY',
  time_format: '12h'
}

const THEMES = [
  { id: 'light',     label: 'Light',         swatches: ['#ffffff', '#f9fafb', '#e5e7eb'] },
  { id: 'dark',      label: 'Dark',          swatches: ['#1a1a2e', '#16213e', '#0f3460'] },
  { id: 'dark-gray', label: 'Dark Gray',     swatches: ['#1e1e1e', '#252525', '#2d2d2d'] },
  { id: 'contrast',  label: 'High Contrast', swatches: ['#000000', '#0a0a0a', '#111111'] },
  { id: 'warm',      label: 'Warm',          swatches: ['#fdf6e3', '#f5efe0', '#ffffff'] },
  { id: 'navy',      label: 'Navy',          swatches: ['#0a192f', '#112240', '#1d3461'] }
]

const ACCENTS = [
  { id: 'purple', label: 'Purple', hex: '#7C3AED' },
  { id: 'blue',   label: 'Blue',   hex: '#2563EB' },
  { id: 'green',  label: 'Green',  hex: '#059669' },
  { id: 'red',    label: 'Red',    hex: '#DC2626' },
  { id: 'orange', label: 'Orange', hex: '#D97706' },
  { id: 'pink',   label: 'Pink',   hex: '#DB2777' },
  { id: 'teal',   label: 'Teal',   hex: '#0891B2' },
  { id: 'slate',  label: 'Slate',  hex: '#475569' }
]

function loadInitial() {
  // Priority: electron saved theme > localStorage > system preference > defaults
  try {
    if (typeof window !== 'undefined' && window.__savedTheme && typeof window.__savedTheme === 'object') {
      return { ...DEFAULTS, ...window.__savedTheme, _userSet: Object.keys(window.__savedTheme).length > 0 }
    }
  } catch {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return { ...DEFAULTS, ...parsed, _userSet: true }
    }
  } catch {}
  let theme = 'light'
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) theme = 'dark'
  } catch {}
  return { ...DEFAULTS, theme, _userSet: false }
}

function applyToDOM(prefs) {
  const root = document.documentElement
  root.setAttribute('data-theme', prefs.theme || 'light')
  root.setAttribute('data-accent', prefs.accent_color || 'purple')
  root.setAttribute('data-size', prefs.font_size || 'medium')
  root.setAttribute('data-radius', prefs.border_radius || 'rounded')
  root.setAttribute('data-shadow', prefs.shadow_style || 'subtle')
  root.setAttribute('data-border', prefs.border_style || 'normal')
  root.setAttribute('data-sidebar-style', prefs.sidebar_style || 'flush')
  root.setAttribute('data-sidebar-width', prefs.sidebar_width || 'normal')
  root.setAttribute('data-density', prefs.table_density || 'comfortable')
  root.setAttribute('data-compact', prefs.compact_mode ? 'true' : 'false')
}

// Apply saved theme as early as possible (before React renders) to avoid flash.
if (typeof document !== 'undefined') {
  try { applyToDOM(loadInitial()) } catch {}
}

const ThemeContext = createContext(null)

export function ThemeProvider({ children }) {
  const [prefs, setPrefs] = useState(() => loadInitial())
  const [loaded, setLoaded] = useState(false)
  const saveTimer = useRef(null)
  const firstLoad = useRef(true)

  // Apply whenever prefs change
  useEffect(() => { applyToDOM(prefs) }, [prefs])

  // Save to localStorage + API (debounced) + Electron store
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return }
    try {
      const { _userSet, ...persist } = prefs
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persist))
      if (window.electronAPI?.saveThemePrefs) window.electronAPI.saveThemePrefs(persist)
    } catch {}
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      try {
        const token = getToken()
        if (!token) return
        // The quick bar (feedback #835) rides on the same preferences row but
        // is saved only by QuickBarCustomizeModal.  This copy may have been
        // hydrated before the person changed their bar elsewhere, so sending
        // it would put the old bar back on their next colour change.  Both
        // keys are dropped: dropping only quick_bar_firm_default would defeat
        // the server's echo rule (routes/profile.js) and make it worse.
        const {
          _userSet, id, user_id, created_at, updated_at,
          quick_bar_items, quick_bar_firm_default,
          ...body
        } = prefs
        await api.put('/api/profile/preferences', body)
      } catch {}
    }, 500)
  }, [prefs])

  // On login: fetch from API and hydrate
  const hydrateFromAPI = useCallback(async () => {
    try {
      const token = getToken()
      if (!token) return
      const res = await api.get('/api/profile/preferences')
      if (res.data) {
        firstLoad.current = true
        setPrefs(p => ({ ...DEFAULTS, ...res.data, _userSet: true }))
      }
    } catch {}
    setLoaded(true)
  }, [])

  useEffect(() => {
    hydrateFromAPI()
    // Listen for login event so we can re-fetch
    const onLogin = () => hydrateFromAPI()
    window.addEventListener('auth-login', onLogin)
    return () => window.removeEventListener('auth-login', onLogin)
  }, [hydrateFromAPI])

  // Listen for system theme changes (if user hasn't set a preference)
  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e) => {
      setPrefs(p => p._userSet ? p : { ...p, theme: e.matches ? 'dark' : 'light' })
    }
    mq.addEventListener?.('change', handler)
    return () => mq.removeEventListener?.('change', handler)
  }, [])

  // Listen for Electron system theme updates
  useEffect(() => {
    if (!window.electronAPI?.onSystemThemeChange) return
    window.electronAPI.onSystemThemeChange((value) => {
      setPrefs(p => p._userSet ? p : { ...p, theme: value === 'dark' ? 'dark' : 'light' })
    })
  }, [])

  const setPref = useCallback((key, value) => {
    setPrefs(p => ({ ...p, [key]: value, _userSet: true }))
  }, [])

  const formatDate = useCallback((d) => fmtDate(d, prefs.date_format), [prefs.date_format])
  const formatTime = useCallback((d) => fmtTime(d, prefs.time_format), [prefs.time_format])

  const value = {
    ...prefs,
    loaded,
    setPref,
    setTheme: (v) => setPref('theme', v),
    setAccentColor: (v) => setPref('accent_color', v),
    setFontSize: (v) => setPref('font_size', v),
    setCompactMode: (v) => setPref('compact_mode', v),
    setBorderRadius: (v) => setPref('border_radius', v),
    setShadowStyle: (v) => setPref('shadow_style', v),
    setBorderStyle: (v) => setPref('border_style', v),
    setSidebarStyle: (v) => setPref('sidebar_style', v),
    setSidebarWidth: (v) => setPref('sidebar_width', v),
    setTableDensity: (v) => setPref('table_density', v),
    setDateFormat: (v) => setPref('date_format', v),
    setTimeFormat: (v) => setPref('time_format', v),
    formatDate,
    formatTime,
    THEMES,
    ACCENTS,
    hydrateFromAPI
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
